// ═══════════════════════════════════════════════════════════
// Request Store — Zustand store for FOIA request data
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { listRequests, type FoiaRequest, type ListRequestsOptions } from '../api/requests';

interface RequestStore {
  requests:    FoiaRequest[];
  loading:     boolean;
  error:       string | null;
  lastFetched: Date | null;

  fetchRequests: (opts?: ListRequestsOptions) => Promise<void>;
  refresh:       () => Promise<void>;
  upsert:        (req: FoiaRequest) => void;
}

let _lastOpts: ListRequestsOptions = { includeClosd: false };

export const useRequestStore = create<RequestStore>((set, get) => ({
  requests:    [],
  loading:     false,
  error:       null,
  lastFetched: null,

  async fetchRequests(opts: ListRequestsOptions = {}) {
    _lastOpts = opts;
    set({ loading: true, error: null });
    try {
      const results = await listRequests(opts);
      set({ requests: results, loading: false, lastFetched: new Date() });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Failed to load requests' });
    }
  },

  async refresh() {
    return get().fetchRequests(_lastOpts);
  },

  upsert(req: FoiaRequest) {
    set(state => {
      const idx = state.requests.findIndex(r => r.recordId === req.recordId);
      if (idx >= 0) {
        const next = [...state.requests];
        next[idx] = req;
        return { requests: next };
      }
      return { requests: [req, ...state.requests] };
    });
  },
}));
